import { useContext } from 'react';

import AppContext from 'App/AppContext';
import {
  IPCheckbox,
  IPCommandBarButton,
  IPIconButton,
  IPLink,
  IPScrollablePane,
  IPSeparator,
  IPText,
} from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import Accordion from 'components/Accordion/Accordion';
import { GlobalFilterGroupKeys } from 'components/GlobalFilter/GlobalFilterGroupKeys';
import {
  clearFilterKeepGlobalFilter,
  countGlobalFilters,
  globalFilterCount,
} from 'components/GlobalFilter/GlobalFilterHelper';
import { FilterStandardsGroupKeys } from 'components/Norm/FilterNormGroupKeys';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { OwnerPicker } from 'components/Pickers/OwnerPicker';
import { getEntityStatusTextList } from 'globalFunctions';
import {
  cancelIcon,
  filterICon,
  globalSceneBarItemStyles,
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { AuthSchema } from 'models/auth/authSchema';
import { Role } from 'models/auth/role';
import Control, { ControlScope } from 'models/control';
import Norm from 'models/norm';
import Tag from 'models/tag';
import User from 'models/user';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';

export const isoNormSearchPrepend = 'SearchByISONorm_';
export const normSearchPrepend = 'SearchByNorm_';

interface IFilterControl {
  toggleDialog: (state: boolean) => void;
  isOpen: boolean;
  updateSelectedFilter: Function;
  selectedFilter: string[];
}
interface ICheckbox {
  key: string;
  value: string;
  label: string;
}
const FilterNorm = (props: IFilterControl) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'norms']);

  const applicabilityFilterList: ICheckbox[] = [
    ...Control.getApplicabilityReasonList(t).map((r) => {
      return {
        key: r.key as string,
        value: r.key as string,
        label: r.text ?? '',
      };
    }),
  ];

  const statuses: ICheckbox[] = [
    ...getEntityStatusTextList(t).map((item) => {
      return {
        key: item.key as string,
        value: item.key as string,
        label: item.text ?? '',
      };
    }),
  ];

  const getCoveredFilterList = (): ICheckbox[] => {
    const items = [
      {
        key: 'noControl',
        value: 'noControl',
        label: Control.getScopeText(ControlScope.NotCovered, t),
      },
      {
        key: 'covered',
        value: 'covered',
        label: Control.getScopeText(ControlScope.Covered, t),
      },
    ];

    if (hasUserFeature(appContext, FeatureTypes.OrgUnits)) {
      items.push({
        key: 'linked',
        value: 'linked',
        label: Control.getScopeText(ControlScope.Linked, t),
      });
    }

    return items;
  };

  const handleFilterClear = () => {
    clearFilter();
    props.toggleDialog(false);
  };

  const clearFilter = () => {
    const newFilter = clearFilterKeepGlobalFilter(appContext, props.selectedFilter, FilterStandardsGroupKeys);
    props.updateSelectedFilter(newFilter);
  };

  const handleSelectChange = (key: string) => {
    const items: string[] = [...props.selectedFilter];
    if (!props.selectedFilter.includes(key)) {
      items.push(key);
      props.updateSelectedFilter(items);
    } else {
      props.updateSelectedFilter(items.filter((i: string) => i !== key));
    }
  };

  const getSelectedFilterCount = (type: FilterStandardsGroupKeys) => {
    if (props.selectedFilter.length > 0) {
      return props.selectedFilter.filter((f) => f.startsWith(type))?.length;
    } else {
      return 0;
    }
  };

  const getTagsFromFilter = (filter: string[]): Tag[] => {
    const tags: Tag[] = [];
    filter.forEach((f) => {
      if (f.startsWith(FilterStandardsGroupKeys.Tag)) {
        const tagId = f.substring(4);
        const tag = appContext.globalDataCache.tags.get(Number.parseInt(tagId));
        tags.push(tag);
      }
    });

    return tags;
  };

  const getUserFromFilter = (filter: string[]): string | undefined => {
    const user = filter.find((f) => f.startsWith(FilterStandardsGroupKeys.Owner));

    return user?.split('$')[1];
  };

  const getFilterCount = (filter: string[]): number => {
    const count = filter.filter(
      (f) => !f.startsWith(FilterStandardsGroupKeys.periodStart) && !f.startsWith(FilterStandardsGroupKeys.periodEnd),
    ).length;

    return count - countGlobalFilters(appContext, filter, FilterStandardsGroupKeys);
  };

  //
  // Main render
  //
  const filterCount = getFilterCount(props.selectedFilter);

  return (
    <IPStack horizontal verticalAlign="center">
      <IPCommandBarButton
        iconProps={filterICon}
        className="calloutTargetFilterButton"
        text={t('norms:TreeList.Filter.Button', { count: filterCount })}
        onClick={() => props.toggleDialog(!props.isOpen)}
        styles={globalSceneBarItemStyles}
      />
      {filterCount > 0 && (
        <IPIconButton styles={globalSceneBarItemStyles} onClick={() => clearFilter()} iconProps={cancelIcon} />
      )}
      <IPCallout
        alignTargetEdge={true}
        gapSpace={0}
        target=".calloutTargetFilterButton"
        isBeakVisible={false}
        directionalHint={IPDirectionalHint.bottomLeftEdge}
        hidden={!props.isOpen}
        onDismiss={() => props.toggleDialog(false)}
      >
        <IPStack styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPStack horizontal horizontalAlign={'space-between'} styles={globalStackStylesHeight100PaddingSmall}>
              <IPText variant={'mediumPlus'}>{t('norms:TreeList.Filter.Header')}</IPText>
              <IPLink onClick={handleFilterClear} underline>
                {t('norms:TreeList.Filter.Clear')}
              </IPLink>
            </IPStack>
          </IPStack.Item>
          <IPStack.Item styles={{ root: { height: 400, width: 300, position: 'relative' } }}>
            <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
              <Accordion
                disabled={globalFilterCount(appContext, GlobalFilterGroupKeys.standard) > 0}
                title={t('norms:TreeList.Filter.ISONorms', {
                  count: getSelectedFilterCount(FilterStandardsGroupKeys.standard),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {appContext.globalDataCache.norms.getItemsISO().map((norm: Norm) => (
                    <IPCheckbox
                      key={norm.isoNormId}
                      label={norm.name}
                      checked={props.selectedFilter.includes(
                        `${FilterStandardsGroupKeys.standard}${isoNormSearchPrepend}${norm.isoNormId}`,
                      )}
                      onChange={() =>
                        handleSelectChange(
                          `${FilterStandardsGroupKeys.standard}${isoNormSearchPrepend}${norm.isoNormId}`,
                        )
                      }
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('norms:TreeList.Filter.Owners', {
                  count: getSelectedFilterCount(FilterStandardsGroupKeys.Owner),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <OwnerPicker
                    hideLabel={true}
                    selectedItemId={getUserFromFilter(props.selectedFilter)}
                    onSelect={(item) => {
                      let id: string | undefined = undefined;
                      if (item instanceof User) {
                        id = item.id;
                      } else if (item instanceof Role) {
                        id = item.roleId;
                      } else if (!item) {
                        return handleSelectChange(
                          FilterStandardsGroupKeys.Owner + getUserFromFilter(props.selectedFilter),
                        );
                      }

                      handleSelectChange(FilterStandardsGroupKeys.Owner + id);
                    }}
                  />
                </IPStack>
              </Accordion>
              {hasUserFeature(appContext, FeatureTypes.RBAC) && (
                <Accordion
                  title={t('adminAuth:AuthSchemaPicker.Filter.Shared', {
                    count: getSelectedFilterCount(FilterStandardsGroupKeys.Shared),
                  })}
                >
                  <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                    <IPCheckbox
                      key={'everyone'}
                      label={t('adminAuth:AuthSchemaPicker.Placeholder')}
                      checked={props.selectedFilter.includes(FilterStandardsGroupKeys.Shared + 'everyone')}
                      onChange={() => {
                        handleSelectChange(FilterStandardsGroupKeys.Shared + 'everyone');
                      }}
                    />
                    <IPSeparator styles={{ root: { height: 1 } }} />
                    {appContext.globalDataCache.authSchemas
                      .getItemsForMember(appContext.user.id)
                      .map((schema: AuthSchema) => (
                        <IPCheckbox
                          key={schema.authSchemaId}
                          label={schema.name}
                          checked={props.selectedFilter.includes(FilterStandardsGroupKeys.Shared + schema.authSchemaId)}
                          onChange={() => handleSelectChange(FilterStandardsGroupKeys.Shared + schema.authSchemaId)}
                        />
                      ))}
                  </IPStack>
                </Accordion>
              )}
              <Accordion
                title={t('norms:TreeList.Filter.Scope', {
                  count: getSelectedFilterCount(FilterStandardsGroupKeys.Scope),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {getCoveredFilterList().map((item: ICheckbox) => (
                    <IPCheckbox
                      key={item.key}
                      label={item.label}
                      checked={props.selectedFilter.includes(FilterStandardsGroupKeys.Scope + item.value)}
                      onChange={() => handleSelectChange(FilterStandardsGroupKeys.Scope + item.value)}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('norms:TreeList.Filter.Reasons', {
                  count: getSelectedFilterCount(FilterStandardsGroupKeys.Applicability),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {applicabilityFilterList.map((item: ICheckbox) => (
                    <IPCheckbox
                      key={item.key}
                      label={item.label}
                      checked={props.selectedFilter.includes(FilterStandardsGroupKeys.Applicability + item.value)}
                      onChange={() => handleSelectChange(FilterStandardsGroupKeys.Applicability + item.value)}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('norms:TreeList.Filter.ImplementationStates', {
                  count: getSelectedFilterCount(FilterStandardsGroupKeys.EntityStatus),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  {statuses.map((item: ICheckbox) => (
                    <IPCheckbox
                      key={item.key}
                      label={item.label}
                      onChange={() => {
                        handleSelectChange(FilterStandardsGroupKeys.EntityStatus + item.key);
                      }}
                      checked={props.selectedFilter.includes(FilterStandardsGroupKeys.EntityStatus + item.key)}
                    />
                  ))}
                </IPStack>
              </Accordion>
              <Accordion
                title={t('norms:TreeList.Filter.Tags', {
                  count: getSelectedFilterCount(FilterStandardsGroupKeys.Tag),
                })}
              >
                <IPStack tokens={globalStackTokensGapSmall} styles={globalStackStylesHeight100PaddingSmall}>
                  <KeyValueTagPicker
                    maxTagWidth={240}
                    selectedTags={getTagsFromFilter(props.selectedFilter)}
                    onAdd={(item: Tag) => handleSelectChange(FilterStandardsGroupKeys.Tag + item.tagId.toString())}
                    onRemove={(item: Tag) => handleSelectChange(FilterStandardsGroupKeys.Tag + item.tagId.toString())}
                  />
                </IPStack>
              </Accordion>
            </IPScrollablePane>
          </IPStack.Item>
        </IPStack>
      </IPCallout>
    </IPStack>
  );
};

export default FilterNorm;
